import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import type { CSSObject, Theme } from '@mui/material/styles'

const SCROLLBAR_SIZE = 10

/**
 * Always-visible scrollbars for a scrolling element. Overlay scrollbars stay invisible until they
 * move, so these are painted rather than native.
 */
export const visibleScrollbarSx = (theme: Theme): CSSObject => {
    const dark = theme.palette.mode === 'dark'
    const thumbColor = dark ? theme.palette.grey[700] : theme.palette.action.active
    const trackColor = dark ? theme.palette.grey[900] : 'transparent'
    return {
        // Chrome ignores the ::-webkit-scrollbar styles below once either standard property is set
        '@supports not selector(::-webkit-scrollbar)': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${thumbColor} ${trackColor}`,
        },
        '&::-webkit-scrollbar': { WebkitAppearance: 'none', width: SCROLLBAR_SIZE, height: SCROLLBAR_SIZE },
        '&::-webkit-scrollbar-track': { backgroundColor: trackColor },
        '&::-webkit-scrollbar-corner': { backgroundColor: theme.palette.background.paper },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: SCROLLBAR_SIZE / 2,
            border: `2px solid ${theme.palette.background.paper}`,
            backgroundColor: thumbColor,
        },
    }
}

const scrollbarSx = (theme: Theme): CSSObject => ({
    overflowX: 'auto',
    overflowY: 'hidden',
    minHeight: SCROLLBAR_SIZE + 2,
    ...visibleScrollbarSx(theme),
})

const hiddenScrollbarSx: CSSObject = {
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    '&::-webkit-scrollbar': { display: 'none' },
}

export interface StickyHorizontalScroll {
    viewportRef: React.RefObject<HTMLDivElement | null>
    onViewportScroll: () => void
    /** Makes the element the horizontal scroller, and hides its own scrollbar once the proxy shows. */
    viewportSx: CSSObject
    /** Place inside a sticky footer; null while the content fits. */
    scrollbar: React.ReactElement | null
    overflowing: boolean
}

/**
 * Drives horizontal scrolling from a scrollbar the caller can keep in view.
 *
 * Content taller than the window puts its own horizontal scrollbar below its last row, so reaching
 * it means scrolling past everything first, and on platforms with overlay scrollbars there is
 * nothing to aim at until the pointer already moves. This keeps the real scroller but hides its
 * scrollbar, driving it from a proxy the caller renders in whichever sticky footer it already has,
 * so the proxy cannot end up stacked behind pagination. It appears only while the content overflows.
 */
export const useStickyHorizontalScroll = (dependency?: unknown): StickyHorizontalScroll => {
    const viewportRef = useRef<HTMLDivElement>(null)
    const proxyRef = useRef<HTMLDivElement>(null)
    const [contentWidth, setContentWidth] = useState(0)
    const [visibleWidth, setVisibleWidth] = useState(0)

    const measure = useCallback(() => {
        const viewport = viewportRef.current
        if (!viewport) return
        setContentWidth(viewport.scrollWidth)
        setVisibleWidth(viewport.clientWidth)
    }, [])

    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) return
        measure()
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(measure)
        observer.observe(viewport)
        // Columns and rows resize the content without resizing the viewport around it.
        for (const child of viewport.children) observer.observe(child)
        return () => observer.disconnect()
    }, [measure, dependency])

    // Assigning scrollLeft raises a scroll event on the other element; the tolerance stops the pair
    // from trading rounded values back and forth forever.
    const mirror = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
        if (!from || !to || Math.abs(to.scrollLeft - from.scrollLeft) < 0.5) return
        to.scrollLeft = from.scrollLeft
    }

    const overflowing = contentWidth > visibleWidth + 1

    return {
        viewportRef,
        onViewportScroll: () => mirror(viewportRef.current, proxyRef.current),
        viewportSx: { overflowX: 'auto', ...(overflowing ? hiddenScrollbarSx : {}) },
        overflowing,
        scrollbar: overflowing ? (
            <Box
                ref={proxyRef}
                aria-hidden
                data-testid="sticky-horizontal-scrollbar"
                onScroll={() => mirror(proxyRef.current, viewportRef.current)}
                sx={scrollbarSx}
            >
                <Box sx={{ width: contentWidth, height: '1px' }} />
            </Box>
        ) : null,
    }
}
